import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateValue } from 'react-aria';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import {
  FilterTagsCondition,
  SegmentedTagsConditionFilter,
} from 'dashboard/components/TagsConditionFilter';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import FormCreateAccount, { FormCreateAccountData } from 'dashboard/forms/FormCreateAccount';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { ExporterFileType } from 'dashboard/models/exporter';
import { List, ListStatus } from 'dashboard/models/list';
import { Tag } from 'dashboard/models/tag';
import { AccountsFilterParams } from 'dashboard/redux/slices/accounts';
import AccountsTable from 'dashboard/views/DashboardAccountsView/AccountsTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import IconAccounts from 'images/icons/accounts.svg';
import IconPlus from 'images/icons/plus.svg';
import IconDownload from 'images/icons/upload.svg';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';

// this is the pagination limit, we should display over 10000 in modal once count is >= 10000
export const WARN_LARGE_EXPORT_CUTOFF = 10000;

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
`;

const ExportButton = styled(Button)`
  margin-left: ${Spacing[3]};
`;

export interface DashboardAccountsViewProps {
  accounts: Account[];
  accountStatuses: AccountStatus[];
  accountTypesFilterOptions: AccountTypesFilterOption[];
  accountPaginationParams: PaginationParams;
  tags: Tag[];
  pageSize: number;
  accountsFilters: AccountsFilterParams;
  sortParams: SortParams;
  isLoadingAccounts: boolean;
  currentEnvironment: Environment;
  featureFlags: FeatureFlagsMap;
  productFeatureIsDisabled: boolean;
  exportersProductFeatureIsDisabled: boolean;
  lists: List[];
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleCreateAccount: (values: FormCreateAccountData) => Promise<void>;
  handleAccountStatusFilterUpdate: (accountStatusIds: string[]) => void;
  handleAccountTypeFilterUpdate: (accountTypeIds: string[]) => void;
  handleTagFilterUpdate: (tagIds: string[]) => void;
  handleTagsConditionsFilterUpdate: (tagsConditions: FilterTagsCondition[][]) => void;
  handleExportData: (exporterFileType: ExporterFileType) => Promise<void>;
  handleListFilterUpdate: (listIds: string[]) => void;
}

const DashboardAccountsView = ({
  accounts,
  accountStatuses,
  accountTypesFilterOptions,
  accountPaginationParams,
  pageSize,
  tags,
  accountsFilters,
  sortParams,
  isLoadingAccounts,
  currentEnvironment,
  featureFlags,
  lists,
  handlePageLinkClick,
  handlePageSizeClick,
  handleDateUpdate,
  handleOmniSearch,
  handleSortUpdate,
  handleCreateAccount,
  handleAccountStatusFilterUpdate,
  handleAccountTypeFilterUpdate,
  handleTagsConditionsFilterUpdate,
  handleExportData,
  handleListFilterUpdate,
  productFeatureIsDisabled,
  exportersProductFeatureIsDisabled,
}: DashboardAccountsViewProps) => {
  const {
    updatedAtStart,
    updatedAtEnd,
    accountStatusIds,
    accountTypeIds,
    tagsConditions,
    omni,
    listIds,
  } = accountsFilters;
  const [createAccountModal, { enable: showCreateAccountModal, disable: hideCreateAccountModal }] =
    useToggle(false);

  const [exporterFileType, setExporterFileType] = useState(ExporterFileType.TarGz);

  const location = useLocation();
  const hasAccountWritePermission = usePermission(Permission.AccountWrite);
  const hasAccountCreatePermission = usePermission(Permission.AccountCreate);
  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateAccountModalParam = queryParams.get('openCreateAccountModal');

    if (
      openCreateAccountModalParam === 'true' &&
      hasAccountCreatePermission &&
      isCurrentEnvironmentActive
    ) {
      showCreateAccountModal();
    }
  }, [location, hasAccountWritePermission, hasAccountCreatePermission, isCurrentEnvironmentActive]);

  const totalAccountsCount = accountPaginationParams?.['total-count'] ?? 0;
  const [showLargeExportModal, { enable: openLargeExportModal, disable: hideLargeExportModal }] =
    useToggle();
  const showListMatchesFilter = useFeatureFlag('dashboard:decouple-list-matches-from-inquiries');

  const handleCreateAccountAndClose = async (values: FormCreateAccountData) => {
    await handleCreateAccount(values);
    hideCreateAccountModal();
  };

  const accountStatusFilterOptions = useMemo(
    () =>
      accountStatuses.map((accountStatus) => ({
        label: accountStatus.attributes.name,
        value: accountStatus.id,
      })),
    [accountStatuses],
  );

  const accountTypeSelectOptions = useMemo(
    () =>
      accountTypesFilterOptions.map((filterOption) => ({
        label: filterOption.name,
        value: filterOption.id,
      })),
    [accountTypesFilterOptions],
  );

  const tagFilterOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [tags],
  );

  const listFilterOptions = useMemo(
    () =>
      (lists || [])
        .filter((list) => list.attributes.status === ListStatus.Active)
        .map((list) => ({
          label: list.attributes.name,
          value: list.id,
        })),
    [lists],
  );

  const listFilterLabelTextMapper = useCallback(
    (values: string[]) => {
      if (values.length === 0 || values.length === listFilterOptions.length) {
        return 'All lists';
      } else if (values.length === 1) {
        return listFilterOptions.find((v) => v.value === values[0])?.label;
      } else {
        return `Matching ${values.length} Lists`;
      }
    },
    [listFilterOptions],
  );

  if (productFeatureIsDisabled) {
    return (
      <>
        <DisabledProductFeature
          title='Accounts'
          headerChildren={
            <ButtonGroup>
              <Permissioned permission={Permission.AccountCreate} fallback={null}>
                <Button
                  id='accounts-view__create-account-button'
                  name='accounts-view__create-account-button'
                  data-test='accounts-view__create-account-button'
                  onClick={showCreateAccountModal}
                  prefixIcon={<IconPlus />}
                  disabled={!isCurrentEnvironmentActive}
                >
                  Create account
                </Button>
              </Permissioned>
            </ButtonGroup>
          }
          prompt='Accounts mirror the users on your system.'
          helpLink={
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360059119913-Accounts-Overview'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about accounts
            </a>
          }
          icon={<IconAccounts />}
          popupMessage={
            productFeatureIsDisabled
              ? 'Your current plan does not have access to this feature.'
              : 'Your current role does not have permissions for this feature'
          }
        />
        <ModalWrapper isOpen={createAccountModal} handleCloseModal={hideCreateAccountModal}>
          <ModalContainer data-test='create-account-modal'>
            <ModalHeader data-test='create-account-modal__header' onClose={hideCreateAccountModal}>
              Create a new account
            </ModalHeader>

            <ModalBody>
              <FormCreateAccount
                accountTypesFilterOptions={accountTypesFilterOptions}
                closeModal={hideCreateAccountModal}
                submit={handleCreateAccountAndClose}
              />
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>
      </>
    );
  }

  const onHandleExportClick = (fileType: ExporterFileType) => {
    setExporterFileType(fileType);
    if (totalAccountsCount >= WARN_LARGE_EXPORT_CUTOFF) {
      openLargeExportModal();
    } else {
      handleExportData(fileType);
    }
  };

  const filters: FilterConfig[] = [
    {
      id: 'status',
      enabled: tags.length >= 1 || featureFlags['dashboard:accounts-show-account-status'],
      name: 'Status',
      type: 'multiselect',
      options: accountStatusFilterOptions,
      onChange: handleAccountStatusFilterUpdate,
      value: accountStatusIds || [],
    },
    {
      id: 'type',
      enabled: featureFlags['dashboard:accounts-show-account-type'],
      name: 'Type',
      type: 'multiselect',
      options: accountTypeSelectOptions,
      onChange: handleAccountTypeFilterUpdate,
      value: accountTypeIds || [],
    },
    {
      id: 'listMatches',
      htmlId: 'accounts-view__list-matches',
      name: 'List matches',
      type: 'multiselect-legacy',
      enabled: showListMatchesFilter && listFilterOptions.length > 1,
      selectOptionValues: listFilterOptions,
      labelTextMapper: listFilterLabelTextMapper,
      onChange: handleListFilterUpdate,
      value: listIds || [],
    },
    {
      id: 'tags',
      enabled: tags.length >= 1,
      name: 'Tags',
      type: 'custom',
      filterElement: (
        <SegmentedTagsConditionFilter
          values={tagsConditions}
          tagOptions={tagFilterOptions}
          onChange={(tagsConditions: FilterTagsCondition[][]) => {
            handleTagsConditionsFilterUpdate(tagsConditions);
          }}
          productName='account'
        />
      ),
      onDelete: () => handleTagsConditionsFilterUpdate([]),
      hasValue: tagsConditions && tagsConditions.length > 0,
    },
    {
      id: 'dateUpdated',
      name: 'Date updated',
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: updatedAtStart,
        endDate: updatedAtEnd,
      },
    },
  ];

  return (
    <Container>
      <ModalWrapper isOpen={createAccountModal} handleCloseModal={hideCreateAccountModal}>
        <ModalContainer data-test='create-account-modal'>
          <ModalHeader data-test='create-account-modal__header' onClose={hideCreateAccountModal}>
            Create a new account
          </ModalHeader>

          <ModalBody>
            <FormCreateAccount
              accountTypesFilterOptions={accountTypesFilterOptions}
              closeModal={hideCreateAccountModal}
              submit={handleCreateAccountAndClose}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={showLargeExportModal} handleCloseModal={hideLargeExportModal}>
        <ConfirmationModal
          cancelAction={hideLargeExportModal}
          confirmAction={() => {
            handleExportData(exporterFileType);
          }}
          header='Large export'
          confirmActionText='Export anyways'
          cancelActionText='Cancel'
        >
          You are exporting <strong>over {WARN_LARGE_EXPORT_CUTOFF}</strong> accounts. This may take
          a long time. We recommend limiting the amount of accounts exported by setting filters.
        </ConfirmationModal>
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      <Header data-test='accounts__header' title='Accounts'>
        <ButtonGroup>
          <Permissioned permission={Permission.AccountCreate}>
            <Button
              id='accounts-view__create-account-button'
              name='accounts-view__create-account-button'
              data-test='accounts-view__create-account-button'
              onClick={showCreateAccountModal}
              prefixIcon={<IconPlus />}
              disabled={!isCurrentEnvironmentActive}
              variant='basic'
            >
              Create account
            </Button>
          </Permissioned>
          {!exportersProductFeatureIsDisabled && (
            <Permissioned permission={Permission.AccountsExport}>
              <DropdownMenu
                trigger={
                  <ExportButton
                    id='accounts-view__export-button'
                    data-test='accounts-view__export-button'
                    variant='basic'
                    prefixIcon={<IconDownload />}
                  >
                    Export
                  </ExportButton>
                }
                align='end'
              >
                {Object.values(ExporterFileType).map((fileType) => (
                  <DropdownMenu.Item
                    id={`accounts-view__export__${fileType}`}
                    onClick={() => {
                      onHandleExportClick(fileType);
                    }}
                    key={`accounts-view__export__${fileType}`}
                  >
                    {`To ${fileType}`}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu>
            </Permissioned>
          )}
        </ButtonGroup>
      </Header>
      <Section>
        <Card>
          <Card.Body>
            <Card.Title>Accounts</Card.Title>
            <TableControls
              searchProps={{
                id: 'accounts-table__search',
                handleInput: handleOmniSearch,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value: omni,
                placeholder: 'Search by reference ID, account ID, name, or email',
                dataTest: 'accounts-view__search-input',
                size: 'md',
              }}
              filters={filters}
            />
            <AccountsTable
              accounts={accounts}
              accountStatuses={accountStatuses}
              accountTypesFilterOptions={accountTypesFilterOptions}
              canSort={isEmpty(omni)}
              featureFlags={featureFlags}
              hasUsedFilters={!Object.values(accountsFilters).every(isEmpty)}
              loading={isLoadingAccounts}
              dataTest='accounts-view__accounts-table'
              sortParams={sortParams}
              handleSortUpdate={handleSortUpdate}
            />
            {accounts.length !== 0 && (
              <Pagination
                pageSize={pageSize}
                paginationParams={accountPaginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardAccountsView;
